import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import {
  GraduationCap,
  Laptop,
  Rocket,
  ChefHat,
  Sprout,
  Clapperboard,
  Timer,
  Zap,
  CalendarClock,
  ReceiptText,
} from "lucide-react";
import { Reveal } from "../landing/Reveal";

const LOANS = [
  { icon: GraduationCap, label: "Education Loans" },
  { icon: Laptop, label: "Laptop Loans" },
  { icon: Rocket, label: "Startup Loans" },
  { icon: ChefHat, label: "Chef Loans" },
  { icon: Sprout, label: "Farmer Loans" },
  { icon: Clapperboard, label: "Creator Loans" },
];

const FEATURES = [
  { icon: Timer, t: "Apply in Minutes" },
  { icon: Zap, t: "Fast Approval" },
  { icon: CalendarClock, t: "Flexible EMI" },
  { icon: ReceiptText, t: "Transparent Rates" },
];

const TERMS = [6, 12, 24];
const APR = 0.09;

export const LoanNova = () => {
  const [amount, setAmount] = useState(50000);
  const [term, setTerm] = useState(12);

  const emi = useMemo(() => {
    const r = APR / 12;
    const p = amount;
    const n = term;
    return Math.round((p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1));
  }, [amount, term]);

  return (
    <section id="loannova" data-testid="loannova-section" className="relative overflow-hidden py-24 sm:py-32">
      <div aria-hidden="true" className="absolute -left-40 top-1/3 h-[440px] w-[440px] rounded-full bg-[#635BFF]/12 blur-[150px]" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <p className="font-jbmono text-xs uppercase tracking-[0.3em] text-[#00D26A]">
              LoanNova
            </p>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Credit built for campus life, not against it.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400 sm:text-lg">
              Six loan types, one transparent rate. Your campus reputation is your
              credit score.
            </p>
            <div className="mt-8 flex flex-wrap gap-2.5">
              {LOANS.map((l) => (
                <span
                  key={l.label}
                  data-testid={`loan-type-${l.label.toLowerCase().split(" ")[0]}`}
                  className="flex items-center gap-2 rounded-full border border-white/10 bg-[#111827] px-4 py-2.5 text-sm font-medium text-slate-200 transition-colors hover:border-[#635BFF]/50"
                >
                  <l.icon className="h-4 w-4 text-[#00D26A]" />
                  {l.label}
                </span>
              ))}
            </div>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {FEATURES.map((f) => (
                <div key={f.t} className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <f.icon className="h-4 w-4 shrink-0 text-[#635BFF]" />
                  <span className="text-xs font-medium text-slate-300">{f.t}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div
              data-testid="loan-calculator"
              className="rounded-[2rem] border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-8 backdrop-blur-2xl sm:p-10"
            >
              <div className="flex items-center justify-between">
                <p className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                  EMI Builder
                </p>
                <span className="rounded-full bg-[#00D26A]/10 px-3 py-1 font-jbmono text-[10px] font-bold uppercase tracking-widest text-[#00D26A]">
                  9% APR flat
                </span>
              </div>

              <div className="mt-8">
                <div className="mb-4 flex items-center justify-between">
                  <label className="text-sm font-semibold text-slate-300">Loan amount</label>
                  <span className="font-jbmono text-lg font-bold text-white" data-testid="loan-amount">
                    ₹{amount.toLocaleString("en-IN")}
                  </span>
                </div>
                <div data-testid="loan-amount-slider">
                  <Slider value={[amount]} onValueChange={([v]) => setAmount(v)} min={5000} max={500000} step={5000} />
                </div>
                <div className="mt-2 flex justify-between font-jbmono text-[10px] text-slate-600">
                  <span>₹5K</span>
                  <span>₹5L</span>
                </div>
              </div>

              <div className="mt-8">
                <label className="text-sm font-semibold text-slate-300">Tenure</label>
                <div className="mt-3 grid grid-cols-3 gap-2" data-testid="loan-term-picker">
                  {TERMS.map((t) => (
                    <button
                      key={t}
                      data-testid={`loan-term-${t}`}
                      onClick={() => setTerm(t)}
                      className={`rounded-full py-2.5 font-jbmono text-sm font-bold transition-colors ${
                        term === t
                          ? "bg-gradient-to-r from-[#635BFF] to-[#00D26A] text-white"
                          : "border border-white/10 bg-white/[0.03] text-slate-400 hover:border-[#635BFF]/50"
                      }`}
                    >
                      {t} mo
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-8 rounded-2xl border border-[#00D26A]/20 bg-[#00D26A]/[0.06] p-6 text-center">
                <p className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                  Your EMI
                </p>
                <motion.p
                  key={emi}
                  initial={{ scale: 0.92, opacity: 0.6 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className="mt-2 font-display text-6xl font-black tracking-tight text-white"
                  data-testid="loan-emi"
                >
                  ₹{emi.toLocaleString("en-IN")}
                </motion.p>
                <p className="mt-2 text-xs text-slate-500">
                  {term} monthly payments · no hidden fees
                </p>
              </div>

              <button
                data-testid="loan-apply-cta"
                className="glow-btn-z mt-7 w-full rounded-full bg-gradient-to-r from-[#635BFF] to-[#00D26A] py-4 font-display text-sm font-bold uppercase tracking-wider text-white"
              >
                Apply in Minutes
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
